import { SignInIdentifier, SignInMode } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BirdWelcome from '@/ui-assets/images/bird-welcome.webp';
import Button from '@/components/Button';
import DynamicT from '@/components/DynamicT';
import TextLink from '@/components/TextLink';
import { useSieMethods } from '@/hooks/use-sie';
import useSocialRegister from '@/hooks/use-social-register';
import type { SocialRelatedUserInfo } from '@/types/guard';
import { trackEvent } from '@/utils/analytics';

import styles from './index.module.scss';
import useBindSocialRelatedUser from './use-social-link-related-user';

type Props = {
  readonly className?: string;
  readonly connectorId: string;
  readonly verificationId: string;
  readonly relatedUser: SocialRelatedUserInfo;
  readonly title: TFuncKey;
};

const getCreateAccountActionText = (signUpMethods: string[]): TFuncKey => {
  if (
    signUpMethods.includes(SignInIdentifier.Email) &&
    signUpMethods.includes(SignInIdentifier.Phone)
  ) {
    return 'action.link_another_email_or_phone';
  }

  if (signUpMethods.includes(SignInIdentifier.Email)) {
    return 'action.link_another_email';
  }

  if (signUpMethods.includes(SignInIdentifier.Phone)) {
    return 'action.link_another_phone';
  }

  return 'action.create_account_without_linking';
};

const SocialLinkAccount = ({ connectorId, verificationId, className, relatedUser, title }: Props) => {
  const { t } = useTranslation();
  const { signUpMethods, signInMode } = useSieMethods();

  const bindSocialRelatedUser = useBindSocialRelatedUser();
  const [isBindingUser, setIsBindingUser] = useState(false);

  const registerWithSocial = useSocialRegister(connectorId);

  const actionText = getCreateAccountActionText(signUpMethods);

  const { type, value } = relatedUser;

  return (
    <div className={classNames(styles.container, className)}>
      <img className={styles.image} src={BirdWelcome} width={225} height={186} alt="" />
      <h1 className={styles.title}>
        <DynamicT forKey={title} />
      </h1>
      <div className={styles.desc}>
        {t('description.social_bind_with_existing', {
          type: t(`description.${type === 'email' ? 'email' : 'phone_number'}`),
          value,
        })}
      </div>

      <Button
        title="action.bind"
        isLoading={isBindingUser}
        onClick={async () => {
          trackEvent(
            'social_link_account',
            'clicked',
            { eventId: 'social_link_account_bind_clicked' },
            {
              connectorId,
              actionType: 'bind',
              relatedUserType: type,
            }
          );
          setIsBindingUser(true);
          await bindSocialRelatedUser(verificationId);
          setIsBindingUser(false);
        }}
      />

      {signInMode !== SignInMode.SignIn && (
        <div className={styles.hint}>
          <div>
            <DynamicT forKey="description.skip_social_linking" />
          </div>
          <TextLink
            text={actionText}
            onClick={() => {
              trackEvent(
                'social_link_account',
                'clicked',
                { eventId: 'social_link_account_create_clicked' },
                {
                  connectorId,
                  actionType: 'create_account',
                  relatedUserType: type,
                }
              );
              void registerWithSocial(verificationId);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SocialLinkAccount;
